/**
 * Tabbed spec groups on the giveaway page.
 *
 * These were an accordion with every panel closed, which buried the most
 * specific detail on the page. As tabs, one group is always open.
 *
 * Progressive enhancement: the markup renders every panel visible with no
 * JS, so the content is readable either way — this only takes over once the
 * element upgrades.
 */
if (!customElements.get('giveaway-tabs')) {
  class GiveawayTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
      if (this.tabs.length === 0) return;

      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.select(index));
        tab.addEventListener('keydown', (event) => this.onKey(event, index));
      });

      this.select(0);
    }

    select(index) {
      this.tabs.forEach((tab, i) => {
        const selected = i === index;
        tab.setAttribute('aria-selected', selected ? 'true' : 'false');
        // Roving tabindex: only the active tab is in the tab order, so arrow
        // keys move between tabs and Tab leaves the group.
        tab.tabIndex = selected ? 0 : -1;
      });

      this.panels.forEach((panel, i) => {
        panel.hidden = i !== index;
      });
    }

    onKey(event, index) {
      const last = this.tabs.length - 1;
      let next = null;

      if (event.key === 'ArrowRight') next = index === last ? 0 : index + 1;
      else if (event.key === 'ArrowLeft') next = index === 0 ? last : index - 1;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = last;
      else return;

      event.preventDefault();
      this.select(next);
      this.tabs[next].focus();
    }
  }

  customElements.define('giveaway-tabs', GiveawayTabs);
}

/**
 * Deferred YouTube embed.
 *
 * Renders a thumbnail and a play button; the iframe is only created on
 * click, so the page doesn't pull YouTube's player payload (and its
 * cookies) for everyone who scrolls past. Uses youtube-nocookie.com.
 *
 * maxresdefault.jpg doesn't exist for every video, so a failed load falls
 * back to hqdefault.jpg, which always does.
 */
if (!customElements.get('giveaway-video')) {
  class GiveawayVideo extends HTMLElement {
    connectedCallback() {
      this.videoId = this.dataset.videoId;
      this.button = this.querySelector('[data-video-play]');
      if (!this.videoId || !this.button) return;

      const img = this.querySelector('img[data-fallback]');
      if (img) {
        img.addEventListener('error', function onError() {
          img.removeEventListener('error', onError);
          img.src = img.dataset.fallback;
        });
      }

      this.button.addEventListener('click', () => this.play());
    }

    play() {
      const iframe = document.createElement('iframe');
      iframe.src =
        'https://www.youtube-nocookie.com/embed/' +
        encodeURIComponent(this.videoId) +
        '?autoplay=1&rel=0&playsinline=1';
      iframe.title = this.dataset.videoTitle || 'Video';
      iframe.allow =
        'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
      iframe.allowFullscreen = true;

      this.button.remove();
      this.appendChild(iframe);
    }
  }

  customElements.define('giveaway-video', GiveawayVideo);
}
